import React, { useMemo } from 'react';
import { Controller, type Control } from 'react-hook-form';

import { Stack } from '@/components/layout';
import { FormInput, FormSelectInput, Text } from '@/components/ui';
import type { SelectOption } from '@/components/ui/select/types';
import { STRINGS } from '@/constants/strings';
import {
  MAX_CONTACT_MESSAGE_LENGTH,
  MAX_CONTACT_NAME_LENGTH,
  type GuardianContactUsForm,
} from '@/features/guardian-profile/schemas/guardian-contact-us-schema';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import { MAX_EMAIL_LENGTH } from '@/validation';

const strings = STRINGS.guardian.contactUs;

type GuardianContactUsFieldsProps = {
  control: Control<GuardianContactUsForm>;
  /** Keep the prefilled email read-only (default: true). */
  emailDisabled?: boolean;
};

const LABEL_VARIANT = 'Font Guardian App/Subtitle/Subtitle 12 Semibold';
const INPUT_VARIANT = 'Font Guardian App/Subtitle/Subtitle 14 Regular';

/**
 * Shared body of the Contact Us form (heading + Name / Email / Question Type / Message).
 * Reused by both the phone screen and the tablet popup so the two stay in sync.
 */
export function GuardianContactUsFields({
  control,
  emailDisabled = true,
}: GuardianContactUsFieldsProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  const questionTypeOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'product', label: strings.questionTypes.product },
      { value: 'coaching', label: strings.questionTypes.coaching },
      { value: 'tournament', label: strings.questionTypes.tournament },
    ],
    [],
  );

  return (
    <Stack gap={scale(12)}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.heading}>
        {strings.heading}
      </Text>

      {/* Name */}
      <Controller
        control={control}
        name="name"
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <FormInput
            label={strings.nameLabel}
            required
            uppercaseLabel={false}
            labelVariant={LABEL_VARIANT}
            inputVariant={INPUT_VARIANT}
            labelGap={scale(2)}
            labelStyle={styles.fieldLabel}
            inputStyle={styles.fieldText}
            contentStyle={styles.fieldContent}
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            placeholder={strings.namePlaceholder}
            autoCapitalize="words"
            maxLength={MAX_CONTACT_NAME_LENGTH}
          />
        )}
      />

      {/* Email (prefilled, read-only) */}
      <Controller
        control={control}
        name="email"
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <FormInput
            label={strings.emailLabel}
            required
            disabled={emailDisabled}
            uppercaseLabel={false}
            labelVariant={LABEL_VARIANT}
            inputVariant={INPUT_VARIANT}
            labelGap={scale(2)}
            labelStyle={styles.fieldLabel}
            inputStyle={styles.fieldText}
            contentStyle={styles.fieldContent}
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            placeholder={strings.emailLabel}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            trim
            maxLength={MAX_EMAIL_LENGTH}
          />
        )}
      />

      {/* Question Type — wheel picker (sheet on phone, popup on tablet) */}
      <Controller
        control={control}
        name="questionType"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormSelectInput
            label={strings.questionTypeLabel}
            required
            uppercaseLabel={false}
            labelVariant={LABEL_VARIANT}
            labelStyle={styles.fieldLabel}
            labelGap={scale(2)}
            options={questionTypeOptions}
            value={value || null}
            onValueChange={onChange}
            placeholder={strings.questionTypePlaceholder}
            presentation="sheet"
            triggerVariant={INPUT_VARIANT}
            triggerStyle={styles.selectTrigger}
            sheetStyle={styles.questionSheet}
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
          />
        )}
      />

      {/* Message (optional) */}
      <Controller
        control={control}
        name="message"
        render={({ field: { value, onChange, onBlur } }) => (
          <FormInput
            label={strings.messageLabel}
            required={false}
            uppercaseLabel={false}
            labelVariant={LABEL_VARIANT}
            inputVariant={INPUT_VARIANT}
            labelGap={scale(2)}
            labelStyle={styles.fieldLabel}
            inputStyle={styles.messageText}
            contentStyle={styles.messageContent}
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder={strings.messagePlaceholder}
            multiline
            numberOfLines={4}
            maxLength={MAX_CONTACT_MESSAGE_LENGTH}
          />
        )}
      />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  heading: {
    color: guardianColors['10'],
  },
  fieldLabel: {
    color: guardianColors['10'],
  },
  fieldText: {
    color: guardianColors['10'],
    marginLeft: 0,
  },
  fieldContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
  },
  selectTrigger: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
  },
  questionSheet: {
    borderTopLeftRadius: scale(20),
    borderTopRightRadius: scale(20),
  },
  messageContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(120),
    alignItems: 'flex-start',
    paddingLeft: scale(12),
    paddingRight: scale(12),
    paddingVertical: scale(8),
  },
  messageText: {
    color: guardianColors['10'],
    marginLeft: 0,
    height: '100%',
    textAlignVertical: 'top',
  },
}));

export default GuardianContactUsFields;
